import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

export default function DailyQuestionShareModal({
  open,
  onClose,
  dailyQuestionId,
  currentUserId,
  availableCrews = [],
  sharedCrewIds = new Set(),
  onShared,
}) {
  const [crews, setCrews] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sharedSummary, setSharedSummary] = useState(null);
  const [pulsePreview, setPulsePreview] = useState(null);
  const [shareResults, setShareResults] = useState(null);

  useEffect(() => {
    if (!open || !currentUserId) return;
    // [DAILY_DECISION_SHARE_TDZ_FIX]
    console.log('[DAILY_DECISION_SHARE_MODAL] mounted ok');
    if (window.trackEvent) {
      window.trackEvent('dq_share_opened', { decision_id: dailyQuestionId, source: 'home' });
    }
    setSharedSummary(null);
    setPulsePreview(null);
    if (Array.isArray(availableCrews) && availableCrews.length > 0) {
      setCrews(availableCrews);
      return;
    }

    const load = async () => {
      const { data: authData } = await supabase.auth.getUser();
      const authUid = authData?.user?.id || null;
      const ids = [currentUserId, authUid].filter(Boolean);
      const { data } = await supabase
        .from('crew_members')
        .select('crew_id, crews(id, name)')
        .in('user_id', ids);
      const rows = (data || [])
        .map((r) => r.crews)
        .filter(Boolean);
      setCrews(rows);
    };
    load();
  }, [open, currentUserId, dailyQuestionId, availableCrews]);

  const sharedCrewIdsSafe = sharedCrewIds instanceof Set ? sharedCrewIds : new Set();

  const toggle = (id) => {
    if (sharedCrewIdsSafe.has(id)) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleShare = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    setError(null);
    try {
      const crew_ids = Array.from(selected);
      console.log('[daily] share_submit_start', { decision_id: dailyQuestionId, crew_ids_count: crew_ids.length });
      const { data, error: fnErr } = await supabase.functions.invoke('share-daily-question', {
        body: { daily_question_id: dailyQuestionId, crew_ids, message },
      });
      if (fnErr && !data) throw fnErr;
      const results = data?.perCrew || data?.results || [];
      const counts = data?.totals || data?.counts || {
        shared: results.filter((r) => r.status === 'shared').length,
        already_shared: results.filter((r) => r.status === 'already_shared').length,
        not_member: results.filter((r) => r.status === 'not_member').length,
        failed: results.filter((r) => r.status === 'failed' || r.status === 'error').length,
      };
      if (window.trackEvent) {
        // Emit per-crew share event
        results.forEach((r) => {
          if (r.status === 'shared') {
            window.trackEvent('dq_share_sent', {
              decision_id: dailyQuestionId,
              crew_id: r.crew_id,
              source: 'home',
              dedupe_key: `dq_share_sent:${dailyQuestionId}:${r.crew_id}:${currentUserId}`,
            });
          }
        });
      }
      const sharedIds = results
        .filter((r) => r.status === 'shared')
        .map((r) => r.crew_id);
      const sharedNames = crews
        .filter((c) => sharedIds.includes(c.id))
        .map((c) => c.name);
      onShared?.(data, sharedNames);
      const shared = counts.shared || 0;
      const already = counts.already_shared || 0;
      const failed = (counts.failed || 0) + (counts.not_member || 0);
      setSharedSummary({ count: shared, already, failed });
      setShareResults(results);
      setSelected(new Set());

      // pulse teaser for first successfully shared crew
      const firstShared = results.find((r) => r.status === 'shared')?.crew_id;
      const firstCrew = crews.find((c) => c.id === firstShared);
      if (firstCrew?.id) {
        const { data: pulse } = await supabase.functions.invoke('get-daily-question-pulse', {
          body: { daily_question_id: dailyQuestionId, crew_id: firstCrew.id },
        });
        if (pulse?.ok) {
          setPulsePreview({
            crewName: firstCrew.name,
            totalVotes: pulse.total_votes,
            crewMembers: pulse.crew_members,
            aPct: pulse.option_a_pct,
            bPct: pulse.option_b_pct,
          });
        }
      }
      // Auto-close only when at least one crew succeeded/already-shared.
      if (shared > 0 || already > 0) {
        setTimeout(() => {
          onClose?.();
        }, 600);
      } else if (data?.ok === false && data?.error) {
        setError(data.error);
      } else {
        setError('No shares were completed. Please try another crew.');
      }
      console.log('[daily] share_submit_end', {
        decision_id: dailyQuestionId,
        shared,
        already_shared: already,
        failed,
      });
    } catch (err) {
      console.error('[DailyQuestionShareModal] share error:', err);
      setError(err?.message || 'Failed to share. Try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;
  if (!dailyQuestionId || !currentUserId) {
    return (
      <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
        <div className="bg-slate-800 w-full max-w-md rounded-2xl border border-slate-700 p-4">
          <div className="text-sm text-slate-300">Unable to load share options.</div>
          <button
            onClick={onClose}
            className="mt-4 w-full rounded-lg bg-slate-700 px-3 py-2 text-sm font-semibold text-slate-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 flex items-center justify-center p-4">
      <div className="bg-slate-800 w-full max-w-md rounded-2xl border border-slate-700 p-4">
        <div className="text-lg font-bold text-white">Share Daily Question</div>
        <div className="text-xs text-slate-400 mt-1">Pick crews to share with</div>

        {sharedSummary && (
          <div className="mt-3 rounded-lg border border-green-500/40 bg-green-900/20 p-3 text-xs text-green-200">
            Shared to {sharedSummary.count} crew{sharedSummary.count === 1 ? '' : 's'}.
            {sharedSummary.already > 0 && (
              <div className="text-[11px] text-slate-300 mt-1">
                {sharedSummary.already} already shared today.
              </div>
            )}
            {sharedSummary.failed > 0 && (
              <div className="text-[11px] text-amber-300 mt-1">
                {sharedSummary.failed} failed to share.
              </div>
            )}
          </div>
        )}

        {pulsePreview && (
          <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900 p-3 text-xs text-slate-200">
            <div className="font-bold text-purple-200">Crew Pulse — {pulsePreview.crewName}</div>
            <div className="mt-1">
              {pulsePreview.totalVotes} / {pulsePreview.crewMembers} voted
            </div>
            <div className="mt-1">Option A: {pulsePreview.aPct}% • Option B: {pulsePreview.bPct}%</div>
          </div>
        )}

        <div className="mt-3 space-y-2 max-h-56 overflow-y-auto">
          {crews.map((c) => {
            const isShared = sharedCrewIdsSafe.has(c.id);
            return (
            <button
              key={c.id}
              onClick={() => toggle(c.id)}
              disabled={isShared}
              className={`w-full text-left px-3 py-2 rounded-lg border ${
                selected.has(c.id)
                  ? 'bg-purple-700/30 border-purple-500 text-white'
                  : 'bg-slate-900 border-slate-700 text-slate-200'
              } ${isShared ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center justify-between">
                <span>{c.name}</span>
                {isShared && (
                  <span className="text-[11px] text-emerald-300 font-semibold">Shared ✅</span>
                )}
              </div>
            </button>
          )})}
          {crews.length === 0 && (
            <div className="text-xs text-slate-400">No crews yet.</div>
          )}
        </div>

        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Optional message"
          className="mt-3 w-full rounded-lg bg-slate-900 border border-slate-700 text-slate-100 text-sm p-2"
          rows={2}
        />

        {error && <div className="mt-2 text-xs text-amber-300">{error}</div>}

        {shareResults && shareResults.length > 0 && (
          <div className="mt-2 text-[11px] text-slate-400">
            {shareResults.map((r) => (
              <div key={r.crew_id}>
                {r.crew_id}: {r.status}
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 rounded-lg bg-slate-700 px-3 py-2 text-sm font-semibold text-slate-200"
          >
            {sharedSummary ? 'Done' : 'Cancel'}
          </button>
          <button
            onClick={handleShare}
            disabled={loading || selected.size === 0}
            // [DAILY_DECISION_SHARE_BUTTON_COLOR]
            className="flex-1 rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-slate-900 hover:bg-amber-400 disabled:opacity-50"
          >
            {loading ? 'Sharing…' : 'Share'}
          </button>
        </div>
      </div>
    </div>
  );
}
